import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { IndexType } from "./IndexType";
import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { TableDefinition, BfxColDef } from "./TableDefinition";
import {
  BenchmarkBlotterFilterOrderStates,
  FilterOrderTypes,
  ForwardRollEnum,
} from "./FilterTypeEnums";
import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { forwardRef, Inject, Injectable, Optional } from "@angular/core";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";

// real service imports

import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";

// Adapters
import { OrdersTableMode } from "./OrdersTableMode";
import { ColumnFilterType } from "./ColumnFilterType";
import {
  COLUMN_ORDER_PASS_THROUGH_PARAMETERS,
  COLUMN_TRADE_DESK_ID,
  COLUMN_TRADE_INSTRUMENT,
} from "./TradeTableDefinitionFactory.constants";
import {
  COLUMN_INTER_DESK_ID,
  COLUMN_INTER_INSTRUMENT,
  COLUMN_INTER_ORDER_PRODUCT,
} from "./InterventionTableDefinitionFactory.constants";
import { FixingTimeAdapter } from "../adapter/benchmark/FixingTimeAdapter";
import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";

import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { BmarkTraderGroupNameHelper } from "./benchmarks/helpers/BmarkTradeGroupNameHelper";
import { ProfileSettingKeys } from "./core/ProfileSettingKeys";
import { SysFeatures } from "./core/SysFeatures";
import { StatusIconsHelper } from "./orders/StatusIconsHelper";
import {
  COLUMN_ORDER_STATE,
  COLUMN_DESK_ID,
  COLUMN_CLIENT_BRANCH,
  COLUMN_ENTRY_USER_ID,
  COLUMN_ORDER_ENTRY_DATE,
  COLUMN_TRADER_GROUP_NAMES,
  COLUMN_ALLOCATION_ID,
  COLUMN_ORDER_TYPE,
  COLUMN_VALUE_DATE,
  FIXING_DATE_COLUMN_PROPERTY,
  FIXING_DATE_UTC_FORMAT,
  REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
  REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
  REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
  TIME_ZONE_UTC,
} from "./OrdersTableDefinitionFactory.constants";

import { PercentAwayHelper } from "./orders/PercentAwayHelper";
import { OrderTypeHelper } from "./orders/OrderTypeHelper";
import { OrderStateHelper } from "./orders/OrderStateHelper";
import { ValueDateHelper } from "./orders/ValueDateHelper";
import { NdfTrackingFwdPtsHelper } from "./orders/NdfTrackingFwdPtsHelper";
import {
  ValueGetterParams,
  ValueFormatterParams,
  ICellRendererParams,
  GetQuickFilterTextParams,
  ITooltipParams,
} from "ag-grid-community";
import { BmarkClientOrdersBlotterMode } from "./benchmarks/enums/BmarkClientOrdersBlotterMode.enum";

import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { PrivatizationHelper } from "./orders/PrivatizationHelper";

// OMS-30890 Per Keith D, "-" is reserved for empty numerical fields.
import {
  EMPTY_AMT_RATE_VALUE,
  EMPTY_TEXT_VALUE,
} from "shared/cubiq-services/model/BaseTableDefinitionFactory";
import { MultiLineTooltipValueGetter } from "./tooltip/MultiLineTooltipValueGetter";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { BfxConfigIDRefHelper } from "../../helper-services/bfx-config-id-ref-helper";
import {
  BMARK_DEFAULT_NAME,
  COLUMN_CLIENT_ID_DATA,
  COLUMN_CLIENT_NAME_DATA,
  COLUMN_DEALT_CURRENCY_DATA,
  COLUMN_DEALT_DIR_DATA,
  COLUMN_INSTRUMENT_DATA,
  COLUMN_ORDER_AMOUNT_DATA,
  COLUMN_ORDER_FIXING_SOURCE_CODE_DATA,
  COLUMN_ORDER_PRODUCT_PRODUCT,
  COLUMN_ORDER_USD_AMOUNT_DATA,
  COLUMN_TRADE_CLIENT_TRADER_DATA,
  COLUMN_TRADE_ORDER_TYPE_DATA,
  COLUMN_TRADE_SALES_GROUP_ID_DATA,
  COLUMN_TRADE_VALUE_DATE_DATA,
} from "./BmarkClientOrdersTableDefinitionFactory.constants";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import {
  BmarkRiskData,
  MULTIPLE_FWDS,
} from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { BMOrderAmountHelper } from "shared/cubiq-services/model/benchmarks/helpers/BMOrderAmountHelper";
import { StaticDataPrivatizationType } from "shared/cubiq-services/model/privatization/enum/StaticDataPrivatizationType";
import { ProductRenderer } from "./orders/ProductRenderer";
import { ProductIntentRenderer } from "./orders/ProductIntentRenderer";
import { ValueDateRenderer } from "./orders/ValueDateRenderer";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";
const STRONG_CELL_CLASS: string = "blotter-bold-column";
const STR_MULTIPLE = "Multiple";

@Injectable()
export class BmarkClientOrdersTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "creationDate", sort: "desc" }];
  protected mode: OrdersTableMode = OrdersTableMode.DEALT;
  private myClientTraderEnabled: boolean;
  private myPrivateCommentsEnabled: boolean;
  private myMarkupEnabled: boolean;
  protected myNdfEnabled: boolean;
  protected myFwdEnabled: boolean;
  protected myTrailingStopEnabled: boolean;
  protected myAutFillEnabled: boolean;
  protected myBmarkEnabled: boolean;
  protected myPartFillDisabledTypeText: string = "";
  private marketBarEnabled: boolean = true;
  private myStatusIconHelper: StatusIconsHelper;
  private dealtPercentAwayHelper: PercentAwayHelper;
  private basePercentAwayHelper: PercentAwayHelper;
  private myOrderTypeHelper: OrderTypeHelper;
  private myOrderStateHelper: OrderStateHelper;
  private myValueDateHelper: ValueDateHelper;
  private myNdfTrackingFwdPointsHelper: NdfTrackingFwdPtsHelper;
  protected privatizationHelper: PrivatizationHelper;
  private orderAmountHelper: BMOrderAmountHelper;

  private myBmarkTradeGroupNameHelper: BmarkTraderGroupNameHelper;
  public bmarkShortNm: string;
  private passThroughParamsEnabled: boolean = true;
  private npvEnabled: boolean = false;
  private enabledOBO: boolean = false;

  constructor(
    profileService: ProfilePromiseActionService,
    // It is not possible to inject against an interface
    @Optional() _ccyLookupService: CcyLookupService,
    @Optional() private _ccyPairLookupService: CcyPairLookupService,
    @Optional() private _clientLookup: ClientLookupService,
    @Optional() private _accountLookup: AccountLookupService,
    @Optional() private _salesGroupLookup: SalesGroupLookupService,
    @Optional() private _traderGroupLookup: TraderGroupLookupService,
    @Optional() private _userLookup: UserLookupService,
    @Optional() private _sysConfigService: SystemConfigPromiseService,
    @Optional() protected _translateService: TranslateService,
    @Optional() protected algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional() protected _rateSourceLookup: RateSourceLookupService,
    @Optional() protected _myAccountLookup: AccountLookupService,
    @Optional() protected _mySalesGroupLookup: SalesGroupLookupService,
    @Optional() protected _myTraderGroupLookup: TraderGroupLookupService,
    @Optional() protected _userLookupService: UserLookupService,
    @Optional() protected _myBasePricingService: BasePricingService,
    @Optional() protected _myDealtPricingService: DealtPricingService,
    @Optional() protected _fixingSourceLookupService: FixingSourceLookupService,
    @Optional()
    protected _currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    @Optional() protected cmp: ClientModelPrivatization,
    @Optional() protected amp: AccountModelPrivatization,
    @Optional() protected ctmp: ClientTraderModelPrivatization,
    @Optional() protected atdlTooltipUiRep: AtdlTooltipUiRep,
    @Optional() @Inject(forwardRef(() => BfxConfigService)) configService,
    @Optional() protected customFieldLookupService: CustomFieldLookupService,
  ) {
    super(profileService);

    this.dealtPercentAwayHelper = new PercentAwayHelper(
      this._myDealtPricingService,
      profileService,
      this._ccyPairLookupService,
    );
    this.basePercentAwayHelper = new PercentAwayHelper(
      this._myBasePricingService,
      profileService,
      this._ccyPairLookupService,
    );
    this.myOrderTypeHelper = new OrderTypeHelper(
      this.myPartFillDisabledTypeText,
      this.algoStrategyLookupService,
    );
    this.myOrderStateHelper = new OrderStateHelper(COLUMN_ORDER_STATE, this._translateService);
    this.myStatusIconHelper = new StatusIconsHelper(
      this._userLookupService,
      this._sysConfigService,
      this._translateService,
      this._myDealtPricingService,
      null, // No TSL support
      this._myTraderGroupLookup,
    );
    this.myValueDateHelper = new ValueDateHelper(this._currencyPairCalendarLookupService);
    this.myNdfTrackingFwdPointsHelper = new NdfTrackingFwdPtsHelper(
      this._ccyPairLookupService,
      this._myBasePricingService,
      this._myDealtPricingService,
    );
    this.myBmarkTradeGroupNameHelper = new BmarkTraderGroupNameHelper(
      this._traderGroupLookup,
      COLUMN_DESK_ID,
    );
    this.privatizationHelper = new PrivatizationHelper(_clientLookup, _accountLookup);
    this.orderAmountHelper = new BMOrderAmountHelper(_ccyLookupService);
    // use ClientOrders Preferences
    this.profileService.getProfileConfig(this.getProfileSettingKey()).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.profileService
      .getProfileConfigUpdates(this.getProfileSettingKey())
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
      });

    configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });

    this.getBmarkOrderDisplayName();
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this._sysConfigService.getSystemConfig(SysFeatures.clientTraderUsersEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.featuresEnablePrivateComments),
      this._sysConfigService.getSystemConfig(SysFeatures.markupEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.ndfEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.fwdEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.passThroughColumEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.bmNPV),
      this.atdlTooltipUiRep.isReady(),
    ];
  }

  protected determineDefaultConfig(defaultConfig: any) {
    super.determineDefaultConfig(defaultConfig);
    if (defaultConfig) {
      if (defaultConfig.dealtMode != null) {
        this.mode = defaultConfig.dealtMode ? OrdersTableMode.DEALT : OrdersTableMode.BASE;
      }
      if (defaultConfig.ndfMode != null && defaultConfig.ndfMode) {
        this.mode =
          this.mode === OrdersTableMode.BASE ? OrdersTableMode.NDF_BASE : OrdersTableMode.NDF_DEALT;
      }
    }
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myClientTraderEnabled = this.valueOn(values[0]);
    this.myPrivateCommentsEnabled = this.valueOn(values[1]);
    this.myMarkupEnabled = this.valueOn(values[2]);
    this.myNdfEnabled = this.valueOn(values[3]);
    this.myFwdEnabled = this.valueOn(values[4]);
    this.passThroughParamsEnabled = this.valueOn(values[5]);
    this.npvEnabled = this.valueOn(values[6]);
  }

  private initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      hide: false,
    };
  }

  public createDef(): TableDefinition {
    super.createDef();
    logger.out.info(`${this.constructor.name}.createDef() ${OrdersTableMode[this.mode]}`);
    let fwdColsVisibleByDefault: boolean = this.myNdfEnabled || this.myFwdEnabled;

    let td = new TableDefinition("primaryKey", "as_sort_id");
    td.setOverwriteKey("orderId");

    // requestSubmissionTime's column
    td.setDefaultSort(1, "desc");

    td.benchmarkTableDefinition = true;

    /**
     * Selection column
     */
    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.sortable = false;
    selectionColumn.suppressMovable = true;
    selectionColumn.suppressResize = false;
    selectionColumn.suppressMenu = true;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.valueGetter = (params) => {
      return this.myStatusIconHelper.getClientBMIcons(params.data);
    };
    selectionColumn.cellRenderer = function (params) {
      return params.value != null ? params.value : "";
    };
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.hide = false;
    selectionColumn.width = this.npvEnabled ? this.fiveWs : this.fourWs;
    selectionColumn.minWidth = this.npvEnabled ? this.fiveWs : this.fourWs;
    selectionColumn.maxWidth = this.fourWs;
    selectionColumn.resizable = false;
    selectionColumn.position = this.position.next();
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.indexType = IndexType.None;
    selectionColumn.tooltipValueGetter = () => null;

    td.addColumn(selectionColumn);

    // order state
    let orderStateColumn: BfxColDef = this.initColdDef();
    orderStateColumn.headerName = "Order State";
    orderStateColumn.indexType = IndexType.String;
    orderStateColumn.field = COLUMN_ORDER_STATE;
    orderStateColumn.minWidth = this.oneWs;
    orderStateColumn.width = this.sevenWs;
    orderStateColumn.columnFilterType = ColumnFilterType.ORDERSTATE;
    orderStateColumn.columnFilterData = BenchmarkBlotterFilterOrderStates;
    orderStateColumn.valueGetter = function (params) {
      return this.myOrderStateHelper.getState(params);
    }.bind(this);
    orderStateColumn.cellClass = ["blotter_left_align_col"];
    if (!this.restoreFromConfig(orderStateColumn)) {
      orderStateColumn.position = this.position.next();
    }

    td.addColumn(orderStateColumn);

    // orderId
    let orderIdColumn: BfxColDef = this.initColdDef();
    orderIdColumn.headerName = "Order ID";
    orderIdColumn.field = "orderId";
    orderIdColumn.minWidth = this.oneWs;
    orderIdColumn.width = BfxConfigIDRefHelper.getOrderRefWs();
    orderIdColumn.columnFilterType = ColumnFilterType.NONE;
    orderIdColumn.valueFormatter = BfxConfigIDRefHelper.orderRefValueFormatter;
    orderIdColumn.getQuickFilterText = BfxConfigIDRefHelper.orderRefValueFormatter;
    if (!this.restoreFromConfig(orderIdColumn)) {
      orderIdColumn.hide = true;
      orderIdColumn.position = this.position.next();
    }

    td.addColumn(orderIdColumn);

    // client name
    let clNm: BfxColDef = this.initColdDef();
    clNm.headerName = "Client Name";
    clNm.field = COLUMN_CLIENT_NAME_DATA;
    clNm.indexType = IndexType.String;
    clNm.minWidth = this.oneWs;
    clNm.width = this.tenWs;
    clNm.columnFilterType = ColumnFilterType.CLIENT;
    clNm.cellClass = ["blotter_left_align_col"];
    clNm.valueGetter = (params) => {
      return this.privatizationHelper.getPrivatizedValue(params, COLUMN_CLIENT_NAME_DATA);
    };
    clNm.getFilterValue = (params) => {
      return this.privatizationHelper.getPrivatizedValue({ data: params }, COLUMN_CLIENT_ID_DATA);
    };
    if (!this.restoreFromConfig(clNm)) {
      clNm.position = this.position.next();
    }

    td.addColumn(clNm);

    // client id
    let clientId: BfxColDef = this.initColdDef();
    clientId.headerName = "Client ID";
    clientId.field = COLUMN_CLIENT_ID_DATA;
    clientId.indexType = IndexType.String;
    clientId.minWidth = this.oneWs;
    clientId.width = this.tenWs;
    clientId.columnFilterType = ColumnFilterType.CLIENTID;
    clientId.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, COLUMN_CLIENT_ID_DATA);
    }.bind(this);
    if (!this.restoreFromConfig(clientId)) {
      clientId.hide = true;
      clientId.position = this.position.next();
    }

    td.addColumn(clientId);

    // account name
    let accName: BfxColDef = this.initColdDef();
    accName.headerName = "Account Name";
    accName.field = "accountName";
    accName.indexType = IndexType.String;
    accName.minWidth = this.oneWs;
    accName.width = this.tenWs;
    accName.columnFilterType = ColumnFilterType.ACCOUNT;
    accName.valueGetter = (params) => {
      if (StaticDataPrivatizationType.hasPrivatization(params.data)) {
        // Privatised value is in the stored Account id/name property - getting name will not do lookup
        return this.privatizationHelper.getPrivatizedValue(params, "accountCode");
      }
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSingleAccount) {
        return risk.firstAccountName;
      }
      return STR_MULTIPLE;
    };
    accName.getFilterValue = (params: any) => {
      // Account Name always filters on account code
      if (params?.allocationData?.length > 1) {
        // For BMs, need to make sure that multiple accounts are supported - filter passes if any account matches
        if (params instanceof BmarkOrderData) {
          const accSet = new Set<string>();
          for (const allocation of params.allocationData) {
            accSet.add(
              this.privatizationHelper.getPrivatizedValue({ data: allocation }, "accountCode"),
            );
          }
          // Return
          return [...accSet];
        }
      }
      return this.privatizationHelper.getPrivatizedValue({ data: params }, "accountCode");
    };
    if (!this.restoreFromConfig(accName)) {
      accName.hide = true;
      accName.position = this.position.next();
    }

    td.addColumn(accName);

    // account id
    let accId: BfxColDef = this.initColdDef();
    accId.headerName = "Account ID";
    accId.field = "accountCode";
    accId.indexType = IndexType.String;
    accId.minWidth = this.oneWs;
    accId.width = this.tenWs;
    accId.columnFilterType = ColumnFilterType.ACCOUNTID;
    accId.valueGetter = (params) => {
      //
      if (StaticDataPrivatizationType.hasPrivatization(params.data)) {
        return this.privatizationHelper.getPrivatizedValue(params, "accountCode");
      }
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSingleAccount) {
        return risk.firstAccountCode;
      }
      return STR_MULTIPLE;
      //return this.privatizationHelper.getPrivatizedValue(params, "accountCode");
    };
    accId.getFilterValue = (params) => {
      // For BMs, need to make sure that multiple accounts are supported - filter passes if any account matches
      if (params instanceof BmarkOrderData) {
        if (params?.allocationData?.length > 1) {
          const accSet = new Set<string>();
          for (const allocation of params.allocationData) {
            accSet.add(
              this.privatizationHelper.getPrivatizedValue({ data: allocation }, "accountCode"),
            );
          }
          // Return
          return [...accSet];
        }
        return this.privatizationHelper.getPrivatizedValue({ data: params }, "accountCode");
      }
    };
    if (!this.restoreFromConfig(accId)) {
      accId.hide = true;
      accId.position = this.position.next();
    }

    td.addColumn(accId);

    // dealt pair
    let instrumentCol: BfxColDef = this.initColdDef();
    instrumentCol.headerName = "Instrument";
    instrumentCol.field = COLUMN_INSTRUMENT_DATA;
    instrumentCol.headerTooltip = "Instrument (Dealt)";
    instrumentCol.indexType = IndexType.String;
    instrumentCol.alias = [COLUMN_TRADE_INSTRUMENT, COLUMN_INTER_INSTRUMENT];
    instrumentCol.minWidth = this.oneWs;
    instrumentCol.width = this.sixWs;
    instrumentCol.omitFromPicker = false;
    instrumentCol.isPriority = true;
    instrumentCol.cellClass = ["blotter_left_align_col" + " " + STRONG_CELL_CLASS];
    instrumentCol.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    instrumentCol.valueGetter = function (params) {
      return params.data.risk.pairName;
    };
    if (!this.restoreFromConfig(instrumentCol)) {
      instrumentCol.hide = false;
      instrumentCol.position = this.position.next();
    }

    td.addColumn(instrumentCol);

    // dealt direction
    const intentCol = this.createIntentColumn();
    td.addColumn(intentCol);

    // Dealt ccy
    let dealtCcy: BfxColDef = this.initColdDef();
    dealtCcy.headerName = "Ccy";
    dealtCcy.field = COLUMN_DEALT_CURRENCY_DATA;
    dealtCcy.headerTooltip = "Dealt Currency";
    dealtCcy.indexType = IndexType.String;
    dealtCcy.minWidth = this.oneWs;
    dealtCcy.width = this.threeWs;
    dealtCcy.valueGetter = function (params) {
      return params.data.risk.fixedCcy;
    };
    dealtCcy.cellClass = ["blotter_center_align_col" + " " + STRONG_CELL_CLASS];
    dealtCcy.isPriority = true;
    dealtCcy.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(dealtCcy)) {
      dealtCcy.position = this.position.next();
    }

    td.addColumn(dealtCcy);

    // dealt entered amount
    const orderAmtCd = this.createOrderAmtColumn();
    td.addColumn(orderAmtCd);

    if (this.npvEnabled) {
      td.addColumn(this.createNPVOrderAmtColumn(orderAmtCd));
    }

    // usd entered amount
    const usdAmtCd: BfxColDef = this.initColdDef();
    usdAmtCd.headerName = "Order Amt USD";
    usdAmtCd.field = COLUMN_ORDER_USD_AMOUNT_DATA;
    usdAmtCd.headerTooltip = "Order Amount in USD Terms";
    usdAmtCd.indexType = IndexType.NaturalString;
    usdAmtCd.minWidth = this.oneWs;
    usdAmtCd.width = this.eightWs;
    usdAmtCd.cellClass = ["blotter_amt_align"];
    usdAmtCd.valueFormatter = this.formatAmts;
    usdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    usdAmtCd.columnFilterData = null;
    usdAmtCd.valueGetter = function (params) {
      const risk: BmarkRiskData = params.data.risk;
      return risk.usdAmount;
    };
    usdAmtCd.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return this.orderAmountHelper.formatUSDAmount(risk);
    };
    usdAmtCd.tooltipValueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return this.orderAmountHelper.formatUSDTooltip(risk);
    };

    if (!this.restoreFromConfig(usdAmtCd)) {
      usdAmtCd.hide = true;
      usdAmtCd.position = this.position.next();
    }

    td.addColumn(usdAmtCd);

    // product spot/fwd/fwd off/ndf etc
    const prodCol = this.createProductColumn();
    td.addColumn(prodCol);

    // order type
    let orderTypeCd: BfxColDef = this.initColdDef();
    orderTypeCd.headerName = "Order Type";
    orderTypeCd.field = COLUMN_ORDER_TYPE;
    orderTypeCd.cellRenderer = "orderTypeRenderer";
    orderTypeCd.indexType = IndexType.String;
    orderTypeCd.alias = [COLUMN_TRADE_ORDER_TYPE_DATA];
    orderTypeCd.minWidth = this.oneWs;
    orderTypeCd.width = this.sixWs;
    orderTypeCd.cellClass = ["blotter_center_align"];
    orderTypeCd.columnFilterType = ColumnFilterType.ORDERTYPE;
    orderTypeCd.columnFilterData = FilterOrderTypes;
    orderTypeCd.valueGetter = function (params) {
      return this.myOrderTypeHelper.getType(params.data);
    }.bind(this);
    orderTypeCd.cellRenderer = (params: ICellRendererParams) => {
      if (params.data && params.value) {
        let orderData = params.data as CubiqTS.OrderDataImpl;
        if (orderData.orderType === CubiqTS.OrderType.BENCHMARK) {
          return this.bmarkShortNm;
        }
      }
      return EMPTY_TEXT_VALUE;
    };

    if (!this.restoreFromConfig(orderTypeCd)) {
      orderTypeCd.hide = true;
      orderTypeCd.position = this.position.next();
    }

    td.addColumn(orderTypeCd);

    // value date / tenor
    // acts on settleTenor as well as displaying label
    const vd = this.createValueDateColumn();
    td.addColumn(vd);

    // trader group
    let dkId: BfxColDef = this.initColdDef();
    dkId.headerName = "Trader Group";
    dkId.field = COLUMN_TRADER_GROUP_NAMES; //COLUMN_DESK_ID;
    dkId.alias = [COLUMN_TRADE_DESK_ID, COLUMN_INTER_DESK_ID];
    dkId.minWidth = this.oneWs;
    dkId.width = this.sevenWs;
    dkId.columnFilterType = ColumnFilterType.TRADERGROUP;
    dkId.valueGetter = (params: ValueGetterParams) => {
      return params.data.blotterMode === BmarkClientOrdersBlotterMode.RISK
        ? this.myBmarkTradeGroupNameHelper.getTraderGroupNames(params)
        : params.data[COLUMN_TRADER_GROUP_NAMES];
    };

    // ((params: ValueGetterParams) => {
    //     return (this.myBmarkTradeGroupNameHelper as BmarkTraderGroupNameHelper).getTraderGroupNames(params);
    // }).bind(this);
    if (!this.restoreFromConfig(dkId)) {
      dkId.position = this.position.next();
    }

    td.addColumn(dkId);

    /**
     * Fixing Code column
     */

    let fixingCodeColumn: BfxColDef = this.initColdDef();

    fixingCodeColumn.headerName = "Fixing Code";
    fixingCodeColumn.field = COLUMN_ORDER_FIXING_SOURCE_CODE_DATA;
    fixingCodeColumn.alias = [COLUMN_ORDER_FIXING_SOURCE_CODE_DATA];
    fixingCodeColumn.minWidth = this.oneWs;
    fixingCodeColumn.width = this.sixWs;
    fixingCodeColumn.omitFromPicker = false;
    fixingCodeColumn.columnFilterType = ColumnFilterType.BM_FIXCODE;
    if (!this.restoreFromConfig(fixingCodeColumn)) {
      fixingCodeColumn.position = this.position.next();
    }

    td.addColumn(fixingCodeColumn);

    /**
     * Fixing Time column
     */
    let fixingTimeColumn: BfxColDef = this.initColdDef();

    fixingTimeColumn.headerName = "Fixing Time";
    fixingTimeColumn.field = "reqFixingTime";
    fixingTimeColumn.columnFilterType = ColumnFilterType.NONE;
    fixingTimeColumn.minWidth = this.oneWs;
    fixingTimeColumn.width = this.fiveWs;
    fixingTimeColumn.valueGetter = ((params: ValueGetterParams) => {
      return this.getFixingTimeValue(params.data, "HH:mm", false, false);
    }).bind(this);
    if (!this.restoreFromConfig(fixingTimeColumn)) {
      fixingTimeColumn.position = this.position.next();
    }

    td.addColumn(fixingTimeColumn);

    // /**
    //  * Fixing Date column
    //  */
    let fixingDateColumn: BfxColDef = this.initColdDef();

    fixingDateColumn.headerName = "Fixing Date";
    fixingDateColumn.field = "reqFixingDate";
    fixingDateColumn.minWidth = this.oneWs;
    fixingDateColumn.width = this.sixWs;
    fixingDateColumn.indexType = IndexType.Double;
    fixingDateColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(fixingDateColumn)) {
      fixingDateColumn.position = this.position.next();
    }

    fixingDateColumn.valueGetter = ((params: ValueGetterParams) => {
      return this.getFixingTimeValue(params.data, "DD-MMM-YYYY", false, false, false);
    }).bind(this);

    td.addColumn(fixingDateColumn);

    /**
     * Fixing Time in UTC terms column
     */
    let fixingTimeUTCColumn: BfxColDef = this.initColdDef();

    fixingTimeUTCColumn.headerName = "Fixing UTC Time";
    fixingTimeUTCColumn.field = "fixingTimeUTC";
    fixingTimeUTCColumn.headerTooltip = "Fixing Time in UTC Terms";
    fixingTimeUTCColumn.minWidth = this.oneWs;
    fixingTimeUTCColumn.width = this.fiveWs;
    fixingTimeUTCColumn.indexType = IndexType.String;
    fixingTimeUTCColumn.columnFilterType = ColumnFilterType.NONE;

    fixingTimeUTCColumn.valueGetter = ((params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data;
      return order.fixingDate;
    }).bind(this);

    fixingTimeUTCColumn.valueFormatter = (params: ValueFormatterParams) => {
      return params.value != null
        ? CubiqTS.moment.utc(params.value).format(FIXING_DATE_UTC_FORMAT)
        : "-";
    };

    if (!this.restoreFromConfig(fixingTimeUTCColumn)) {
      fixingTimeUTCColumn.hide = true;
      fixingTimeUTCColumn.position = this.position.next();
    }

    td.addColumn(fixingTimeUTCColumn);

    /**
     * Market spot column
     */
    let marketSpotColumn: BfxColDef = this.initColdDef();

    marketSpotColumn.headerName = "Market Spot";
    marketSpotColumn.minWidth = this.oneWs;
    marketSpotColumn.width = this.sevenWs;
    marketSpotColumn.field = "marketFillSptRt";
    marketSpotColumn.cellClass = ["blotter_amt_align"];
    marketSpotColumn.indexType = IndexType.NaturalString;
    marketSpotColumn.columnFilterType = ColumnFilterType.NONE;

    marketSpotColumn.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data as CubiqTS.OrderDataImpl;
      let value: CubiqTS.BigNumber = null;

      if (order.allocationData != null && order.allocationData[0].fillDetails != null) {
        let fillDetails: CubiqTS.LastFillDetailsImpl = order.allocationData[0]
          .fillDetails as CubiqTS.LastFillDetailsImpl;
        value = fillDetails.marketFillSptRt;
      }

      return value;
    };

    marketSpotColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(marketSpotColumn)) {
      marketSpotColumn.position = this.position.next();
    }

    td.addColumn(marketSpotColumn);

    /**
     * Client Spot column
     */
    let clientSpotColumn: BfxColDef = this.initColdDef();

    clientSpotColumn.headerName = "Client Spot";
    clientSpotColumn.field = "clientFillSptRt";
    clientSpotColumn.minWidth = this.oneWs;
    clientSpotColumn.width = this.sevenWs;
    clientSpotColumn.cellClass = ["blotter_amt_align"];
    clientSpotColumn.indexType = IndexType.NaturalString;
    clientSpotColumn.columnFilterType = ColumnFilterType.NONE;

    clientSpotColumn.valueGetter = (params: ValueGetterParams) => {
      const risk: BmarkRiskData = params.data.risk;
      return risk.getClientSpot();
    };

    clientSpotColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(clientSpotColumn)) {
      clientSpotColumn.hide = true;
      clientSpotColumn.position = this.position.next();
    }

    td.addColumn(clientSpotColumn);

    /**
     * Markup column
     */
    // Sales Margin
    let salesMarginColumn: BfxColDef = this.initColdDef();

    salesMarginColumn.headerName = "Markup";
    salesMarginColumn.field = "dealtMarkup";
    salesMarginColumn.minWidth = this.oneWs;
    salesMarginColumn.width = this.sixWs;
    salesMarginColumn.cellClass = ["blotter_amt_align"];
    salesMarginColumn.indexType = IndexType.String;
    salesMarginColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(salesMarginColumn)) {
      salesMarginColumn.hide = true;
      salesMarginColumn.position = this.position.next();
    }

    salesMarginColumn.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (
        params.data.blotterMode === BmarkClientOrdersBlotterMode.RISK &&
        params.data.allocationData != null &&
        params.data.allocationData.length > 0
      ) {
        let allocation: CubiqTS.AllocationDataImpl = params.data.allocationData[0];
        if (
          allocation.allocationType == CubiqTS.AllocationType.COMPONENT ||
          allocation.allocationType == CubiqTS.AllocationType.UNSPAWNEDCOMPONENT
        ) {
          return "";
        }
      }

      let risk: BmarkRiskData = params.data.risk;
      if (risk.isMultiAllocation) {
        return STR_MULTIPLE;
      }

      if (
        order.orderState === CubiqTS.OrderState.FILLED ||
        order.orderState === CubiqTS.OrderState.PENDING_FWDS
      ) {
        return this._myDealtPricingService.getDealtMarkup(order) as string;
      } else {
        return this._myDealtPricingService.getEntryMarkup(order) as string;
      }
    };

    td.addColumn(salesMarginColumn);

    // entry user name
    let enUser: BfxColDef = this.initColdDef();
    enUser.headerName = "Entry User";
    enUser.field = COLUMN_ENTRY_USER_ID;
    enUser.minWidth = this.oneWs;
    enUser.width = this.sixWs;
    enUser.columnFilterType = ColumnFilterType.USER;
    enUser.valueGetter = function (params) {
      let user = this._userLookupService.getItem(params.data[COLUMN_ENTRY_USER_ID]);
      if (user) {
        return user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    enUser.valueFormatter = function (params) {
      let user = this._userLookupService.getItem(params.data[COLUMN_ENTRY_USER_ID]);
      if (user) {
        return this.showUserName ? user.userName : user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);

    if (!this.restoreFromConfig(enUser)) {
      enUser.position = this.position.next();
    }

    td.addColumn(enUser);

    // entry date
    let entryDate: BfxColDef = this.initColdDef();
    entryDate.headerName = "Entry Date";
    entryDate.field = COLUMN_ORDER_ENTRY_DATE;
    entryDate.minWidth = this.oneWs;
    entryDate.width = this.sixWs;
    entryDate.columnFilterType = ColumnFilterType.NONE;
    entryDate.valueFormatter = this.formatMomment.bind(this);
    if (!this.restoreFromConfig(entryDate)) {
      entryDate.hide = true;
      entryDate.position = this.position.next();
    }

    td.addColumn(entryDate);

    // channel
    let channel: BfxColDef = this.initColdDef();
    channel.headerName = "Channel";
    channel.field = "channelCd";
    channel.headerTooltip = "Entry Channel";
    channel.minWidth = this.oneWs;
    channel.width = this.fiveWs;
    channel.columnFilterType = ColumnFilterType.CHANNEL;
    if (!this.restoreFromConfig(channel)) {
      channel.hide = true;
      channel.position = this.position.next();
    }
    channel.valueGetter = (params) => {
      let channelCd: string;
      channelCd = params.data.channelCd;
      return channelCd != null && typeof channelCd === "string" && channelCd.length > 0
        ? channelCd
        : null;
    };
    channel.valueFormatter = (params) => {
      let channelCd: string = params.data.channelCd;
      let displayName: string;
      if (channelCd != null && typeof channelCd === "string" && channelCd.length > 0) {
        displayName = this._clientLookup.getTradingChannelProfileDisplayName(channelCd);
      }

      return displayName != null && typeof displayName === "string" && displayName.length > 0
        ? displayName
        : EMPTY_TEXT_VALUE;
    };

    td.addColumn(channel);

    // clOrderId
    let clientOrderId: BfxColDef = this.initColdDef();
    clientOrderId.headerName = "ClOrderId";
    clientOrderId.field = "clOrdID";
    clientOrderId.minWidth = this.oneWs;
    clientOrderId.width = this.sixWs;
    clientOrderId.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clientOrderId)) {
      clientOrderId.hide = true;
      clientOrderId.position = this.position.next();
    }

    td.addColumn(clientOrderId);

    // client branch
    let clientBranch: BfxColDef = this.initColdDef();
    clientBranch.headerName = "Branch";
    clientBranch.field = COLUMN_CLIENT_BRANCH;
    clientBranch.headerTooltip = "Client Branch";
    clientBranch.minWidth = this.oneWs;
    clientBranch.width = this.fourWs;
    clientBranch.columnFilterType = ColumnFilterType.BRANCH;
    if (!this.restoreFromConfig(clientBranch)) {
      clientBranch.hide = true;
      clientBranch.position = this.position.next();
    }

    td.addColumn(clientBranch);

    // salesGroup
    let salesGroup: BfxColDef = this.initColdDef();
    salesGroup.headerName = "Sales Group";
    salesGroup.field = COLUMN_TRADE_SALES_GROUP_ID_DATA;
    salesGroup.minWidth = this.oneWs;
    salesGroup.width = this.sevenWs;
    salesGroup.columnFilterType = ColumnFilterType.SALESGROUP;
    salesGroup.valueGetter = function (params) {
      let sg = this._mySalesGroupLookup.getSalesGroup(
        params.data[COLUMN_TRADE_SALES_GROUP_ID_DATA],
      );
      if (sg) {
        return sg.groupCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    if (!this.restoreFromConfig(salesGroup)) {
      salesGroup.hide = true;
      salesGroup.position = this.position.next();
    }

    td.addColumn(salesGroup);

    // client comments
    let clComm1: BfxColDef = this.initColdDef();

    (clComm1.headerName = "Client Comments"), (clComm1.field = "comments1");

    clComm1.minWidth = this.oneWs;
    clComm1.width = this.nineWs;
    clComm1.showTooltip = true;
    clComm1.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clComm1)) {
      clComm1.hide = true;
      clComm1.position = this.position.next();
    }

    td.addColumn(clComm1);

    if (this.myPrivateCommentsEnabled) {
      // private comments
      let clComm2: BfxColDef = this.initColdDef();
      clComm2.headerName = "Private Comments";
      clComm2.field = "comments2";
      clComm2.minWidth = this.oneWs;
      clComm2.width = this.nineWs;
      clComm2.showTooltip = true;
      clComm2.columnFilterType = ColumnFilterType.NONE;
      // clComm2.position = this.position.next(
      if (!this.restoreFromConfig(clComm2)) {
        clComm2.hide = true;
        clComm2.position = this.position.next();
      }

      td.addColumn(clComm2);
    }

    if (this.myClientTraderEnabled) {
      // Client Trader
      let clTrader: BfxColDef = this.initColdDef();
      clTrader.headerName = "Client Trader";
      clTrader.field = COLUMN_TRADE_CLIENT_TRADER_DATA;
      clTrader.alias = [COLUMN_TRADE_CLIENT_TRADER_DATA];
      clTrader.minWidth = this.oneWs;
      clTrader.width = this.sevenWs;
      clTrader.columnFilterType = ColumnFilterType.USER;
      clTrader.columnFilterData = CubiqTS.UserType.CLIENT_TRADER;
      clTrader.valueGetter = function (params) {
        return this.privatizationHelper.getPrivatizedValue(params, COLUMN_TRADE_CLIENT_TRADER_DATA);
      }.bind(this);
      if (!this.restoreFromConfig(clTrader)) {
        clTrader.hide = true;
        clTrader.position = this.position.next();
      }

      td.addColumn(clTrader);
    }

    // STP
    let stph: BfxColDef = this.initColdDef();
    stph.headerName = "Hold STP";
    stph.field = "stpHold";
    stph.minWidth = this.oneWs;
    stph.width = this.sixWs;
    stph.omitFromPicker = false;
    stph.valueFormatter = this.formatBoolean;
    stph.columnFilterType = ColumnFilterType.BOOLEAN;
    stph.cellClass = ["blotter_center_align_col"];

    if (!this.restoreFromConfig(stph)) {
      stph.hide = true;
      stph.position = this.position.next();
    }
    td.addColumn(stph);

    /**
     * Trade id column
     */
    let tradeIdColumn: BfxColDef = this.initColdDef();

    tradeIdColumn.headerName = "Trade ID";
    tradeIdColumn.field = "dealId";
    tradeIdColumn.indexType = IndexType.String;
    tradeIdColumn.minWidth = this.twoWs;
    tradeIdColumn.width = BfxConfigIDRefHelper.getAllocationRefWs();
    tradeIdColumn.columnFilterType = ColumnFilterType.NONE;

    tradeIdColumn.valueGetter = (params: ValueGetterParams) => {
      let dealId: string;
      let risk: BmarkRiskData = params.data.risk;
      if (!risk.isMultiAllocation) {
        dealId = risk.dealRef() ?? EMPTY_TEXT_VALUE;
      } else {
        // If there is any deal, there will be multiple
        if (risk.hasDeal()) {
          return STR_MULTIPLE;
        } else {
          dealId = EMPTY_TEXT_VALUE;
        }
      }
      return dealId;
    };
    // tradeIdColumn.valueFormatter = BfxConfigIDRefHelper.orderAllocationDealRefValueFormatter;
    // tradeIdColumn.getQuickFilterText = BfxConfigIDRefHelper.orderAllocationDealRefValueFormatter;
    if (!this.restoreFromConfig(tradeIdColumn)) {
      tradeIdColumn.hide = true;
      tradeIdColumn.position = this.position.next();
    }

    td.addColumn(tradeIdColumn);

    /**
     * Allocation id column
     */
    let allocIdColumn = this.initColdDef();

    allocIdColumn.headerName = "Allocation ID";
    allocIdColumn.field = COLUMN_ALLOCATION_ID;
    allocIdColumn.minWidth = this.oneWs;
    allocIdColumn.width = this.oneWs;
    allocIdColumn.indexType = IndexType.String;
    allocIdColumn.columnFilterType = ColumnFilterType.NONE;
    allocIdColumn.valueGetter = (params: ValueGetterParams) => {
      let risk: BmarkRiskData = params.data.risk;
      if (!risk.isMultiAllocation) {
        return risk.allocationRef();
      } else {
        return STR_MULTIPLE;
      }
    };
    // allocIdColumn.valueFormatter = BfxConfigIDRefHelper.orderAllocationRefValueFormatter;
    // allocIdColumn.getQuickFilterText = BfxConfigIDRefHelper.orderAllocationRefValueFormatter;
    if (!this.restoreFromConfig(allocIdColumn)) {
      allocIdColumn.hide = true;
      allocIdColumn.position = this.position.next();
    }

    td.addColumn(allocIdColumn);

    /**
     * Deal state column
     */
    let dealStateColumn: BfxColDef = this.initColdDef();

    dealStateColumn.headerName = "Deal State";
    dealStateColumn.field = "dealState";
    dealStateColumn.minWidth = this.oneWs;
    dealStateColumn.columnFilterType = ColumnFilterType.NONE;
    dealStateColumn.columnFilterData = CubiqTS.OrderDealState;
    dealStateColumn.width = this.sevenWs;
    dealStateColumn.valueGetter = (params: ValueGetterParams) => {
      let risk: BmarkRiskData = params.data.risk;
      if (risk.hasDeal()) {
        let dealState = risk.dealState();
        if (dealState !== null) {
          if (dealState === undefined) {
            return "-";
          }

          const lookupId = "OrderDealState." + CubiqTS.OrderDealState[dealState];
          return this._translateService.instant(lookupId);
        }
        // Null dealState implies not all deals have same state
        return STR_MULTIPLE;
      }
      return "";
    };
    if (!this.restoreFromConfig(dealStateColumn)) {
      dealStateColumn.hide = true;
      dealStateColumn.position = this.position.next();
    }

    td.addColumn(dealStateColumn);

    /**
     * Fill date
     */
    let fillDateColumn = this.initColdDef();

    fillDateColumn.headerName = "Fill Date";
    fillDateColumn.field = "fillDts";
    fillDateColumn.minWidth = this.oneWs;
    fillDateColumn.width = this.eightWs;
    fillDateColumn.indexType = IndexType.Double;
    fillDateColumn.omitFromPicker = false;
    fillDateColumn.columnFilterType = ColumnFilterType.NONE;
    fillDateColumn.valueFormatter = this.formatDate.bind(this, "DD-MMM-YY HH:mm:ss");
    fillDateColumn.valueGetter = (params: ValueGetterParams) => {
      let risk: BmarkRiskData = params.data.risk;
      let fillDate = risk.lastFillDate();
      return fillDate;
    };

    if (!this.restoreFromConfig(fillDateColumn)) {
      fillDateColumn.hide = true;
      fillDateColumn.position = this.position.next();
    }

    td.addColumn(fillDateColumn);

    // Pass-Through Params
    if (this.passThroughParamsEnabled) {
      let passThroughParameters: BfxColDef = this.initColdDef();
      passThroughParameters.headerName = "Pass-Through Parameters";
      passThroughParameters.colSelectorName = "Pass-Through ...";
      passThroughParameters.field = COLUMN_ORDER_PASS_THROUGH_PARAMETERS;
      //passThroughParameters.sortingOrder = [];
      passThroughParameters.minWidth = this.oneWs;
      passThroughParameters.width = this.twelveWs;
      passThroughParameters.columnFilterType = ColumnFilterType.NONE;
      passThroughParameters.valueGetter = function (params: ValueGetterParams) {
        let order: CubiqTS.OrderDataImpl = params.data;
        if (
          order.passthroughParameters == null ||
          !Array.isArray(order.passthroughParameters) ||
          order.passthroughParameters.length < 1
        ) {
          return EMPTY_TEXT_VALUE;
        }

        let passThroughParametersValues: CubiqTS.StrategyParameterImpl[] =
          this.atdlTooltipUiRep.inlineUiRep("Pass-Through Parameters", order.passthroughParameters);
        let value = "";

        if (passThroughParametersValues != null && passThroughParametersValues.length > 0) {
          passThroughParametersValues = passThroughParametersValues.sort(
            (a: CubiqTS.StrategyParameterImpl, b: CubiqTS.StrategyParameterImpl) =>
              a.parameterName == null ? -1 : a.parameterName.localeCompare(b.parameterName),
          );
        }

        if (passThroughParametersValues != null && Array.isArray(passThroughParametersValues)) {
          for (const [i, passThroughParamItem] of passThroughParametersValues.entries()) {
            value += `${passThroughParamItem.parameterName}: ${passThroughParamItem.parameterValue}`;

            if (i < passThroughParametersValues.length - 1) value += `; `;
          }
        }
        return value;
      }.bind(this);

      passThroughParameters.tooltipValueGetter = MultiLineTooltipValueGetter;
      if (!this.restoreFromConfig(passThroughParameters)) {
        passThroughParameters.hide = true;
        passThroughParameters.position = this.position.next();
      }

      td.addColumn(passThroughParameters);
    }

    this.position.set(42);
    const mktPtsCol = this.createMarketPtsColumn();
    td.addColumn(mktPtsCol);

    const mktAllInCol = this.createMarketAllInColumn();
    td.addColumn(mktAllInCol);

    const clientPtsCol = this.createClientPtsColumn();
    td.addColumn(clientPtsCol);

    const clientAllInCol = this.createClientAllInColumn();
    td.addColumn(clientAllInCol);

    const marketSwapPtsCol = this.createMarketSwapPtsColumn();
    td.addColumn(marketSwapPtsCol);

    const clientSwapPtsCol = this.createClientSwapPtsColumn();
    td.addColumn(clientSwapPtsCol);

    // Reset here for new columns
    this.position.set(50);

    // OBO Client
    if (this.enabledOBO) {
      let oboClient: BfxColDef = this.initColdDef();
      oboClient.headerName = "OBO Client";
      oboClient.field = "oboClient";
      oboClient.minWidth = this.oneWs;
      oboClient.width = this.sevenWs;
      oboClient.columnFilterType = ColumnFilterType.NONE;
      oboClient.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboClient)) {
        oboClient.hide = true;
        oboClient.position = this.position.next();
      }

      td.addColumn(oboClient);

      // OBO Account
      let oboAccount: BfxColDef = this.initColdDef();
      oboAccount.headerName = "OBO Account";
      oboAccount.field = "oboAccount";
      oboAccount.minWidth = this.oneWs;
      oboAccount.width = this.sevenWs;
      oboAccount.columnFilterType = ColumnFilterType.NONE;
      oboAccount.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboAccount)) {
        oboAccount.hide = true;
        oboAccount.position = this.position.next();
      }

      td.addColumn(oboAccount);

      let oboEntryUser: BfxColDef = this.initColdDef();
      oboEntryUser.headerName = "OBO Entry User";
      oboEntryUser.field = "oboEntryUser";
      oboEntryUser.minWidth = this.oneWs;
      oboEntryUser.width = this.sevenWs;
      oboEntryUser.columnFilterType = ColumnFilterType.NONE;
      oboEntryUser.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboEntryUser)) {
        oboEntryUser.hide = true;
        oboEntryUser.position = this.position.next();
      }

      td.addColumn(oboEntryUser);
    }

    // Forward Roll
    const forwardRoll: BfxColDef = this.initColdDef();
    forwardRoll.headerName = "Forward Roll";
    forwardRoll.field = "autoForwardPricingFg";
    forwardRoll.minWidth = this.oneWs;
    forwardRoll.width = this.sevenWs;
    forwardRoll.valueFormatter = (params: ValueFormatterParams) => {
      let retVal = "-";
      if (params.value != null) {
        if (params.value == true) {
          retVal = "Auto";
        } else {
          retVal = "Manual";
        }
      }
      return retVal;
    };
    forwardRoll.columnFilterType = ColumnFilterType.ENUM;
    forwardRoll.columnFilterData = ForwardRollEnum;
    forwardRoll.getFilterValue = (order: CubiqTS.OrderDataImpl) => {
      let retVal: string = ForwardRollEnum[ForwardRollEnum.NONE];
      if (order?.autoForwardPricingFg != null) {
        retVal =
          order.autoForwardPricingFg === true
            ? ForwardRollEnum[ForwardRollEnum.AUTO]
            : ForwardRollEnum[ForwardRollEnum.MANUAL];
      }
      return retVal;
    };

    if (!this.restoreFromConfig(forwardRoll)) {
      forwardRoll.hide = true;
      forwardRoll.position = this.position.next();
    }

    td.addColumn(forwardRoll);

    // Netting ID (Portfolio ID) Col
    const nettingId: BfxColDef = this.initColdDef();
    nettingId.headerName = "Spot Portfolio";
    nettingId.field = "nettingId";
    nettingId.minWidth = this.oneWs;
    nettingId.width = this.sevenWs;
    nettingId.columnFilterType = ColumnFilterType.NONE;
    nettingId.valueFormatter = (params: ValueFormatterParams) => {
      return params.value == null ? "-" : params.value;
    };

    if (!this.restoreFromConfig(nettingId)) {
      nettingId.hide = true;
      nettingId.position = this.position.next();
    }

    td.addColumn(nettingId);

    // FWD Netting ID (FWD Portfolio ID) Col
    const fwdNettingId: BfxColDef = this.initColdDef();
    fwdNettingId.headerName = "Forward Portfolio";
    fwdNettingId.field = "fwdNettingId";
    fwdNettingId.minWidth = this.oneWs;
    fwdNettingId.width = this.sevenWs;
    fwdNettingId.columnFilterType = ColumnFilterType.NONE;
    fwdNettingId.valueFormatter = (params: ValueFormatterParams) => {
      return params.value == null ? "-" : params.value;
    };

    if (!this.restoreFromConfig(fwdNettingId)) {
      fwdNettingId.hide = true;
      fwdNettingId.position = this.position.next();
    }

    td.addColumn(fwdNettingId);

    // Custom fields
    let custFieldDefns: Array<CubiqTS.CustomFieldImpl> =
      this.customFieldLookupService.getAllCustomFields();
    custFieldDefns.forEach((item) => {
      if (item.isEnabledFg) {
        let customField: BfxColDef = this.initColdDef();
        customField.headerName = item.displayName;
        customField.field = item.fieldId;
        customField.minWidth = this.oneWs;
        customField.width = this.sevenWs;
        customField.columnFilterType = ColumnFilterType.NONE;
        customField.valueGetter = function (params) {
          let order: CubiqTS.OrderDataImpl = params.data;
          if (order != null) {
            let customValues: Array<CubiqTS.CustomValueImpl> = order.customValues;
            if (customValues != null) {
              for (let i: number = 0; i < customValues.length; i++) {
                if (customValues[i].fieldId === customField.field) {
                  return customValues[i].value;
                }
              }
            }
          }
          return EMPTY_TEXT_VALUE;
        }.bind(this);

        if (!this.restoreFromConfig(customField)) {
          customField.hide = true;
          customField.position = 70 + this.customFieldLookupService.getCustomFieldOrdinal(item);
          if (customField.position == null) {
            customField.position = this.position.next();
          }
        }

        td.addColumn(customField);
      }
    });

    // Reset here for new columns
    this.position.set(80);

    this.processQuickFilterColumns(td);

    return td;
  }
  createValueDateColumn(): BfxColDef {
    let vd: BfxColDef = this.initColdDef();
    vd.headerName = "Value Date";
    vd.field = COLUMN_VALUE_DATE;
    vd.alias = [COLUMN_TRADE_VALUE_DATE_DATA];
    vd.minWidth = this.oneWs;
    vd.width = this.sixWs;
    vd.sortKey = "valueDateSort";
    vd.indexType = IndexType.AgGridDefault;
    vd.columnFilterType =
      this.myFwdEnabled || this.myNdfEnabled ? ColumnFilterType.DATE_TENOR : ColumnFilterType.NONE;
    vd.columnFilterData = this.myFwdEnabled || this.myNdfEnabled ? CubiqTS.Tenor : null;
    vd.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return risk.valueDate;
    };

    vd.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return ValueDateRenderer.render(
        params.value,
        (risk && risk.settleTenor) || params.data.settleTenor,
        risk ? risk.isSwap : false,
        risk ? risk.isSSP : false,
        risk ? risk.nearTenor : null,
        risk ? risk.nearValueDate : null,
        risk ? risk.farTenor : null,
        risk ? risk.farValueDate : null,
        this.myValueDateHelper,
      );
    };

    if (!this.restoreFromConfig(vd)) {
      vd.position = this.position.next();
    }

    return vd;
  }

  createOrderAmtColumn(): BfxColDef {
    let orderAmtCd: BfxColDef = this.initColdDef();
    orderAmtCd.headerName = "Order Amt";
    orderAmtCd.field = COLUMN_ORDER_AMOUNT_DATA;
    orderAmtCd.headerTooltip = "Order Amount (Dealt)";
    orderAmtCd.indexType = IndexType.NaturalString;
    orderAmtCd.minWidth = 90;
    orderAmtCd.width = this.eightWs;
    orderAmtCd.isPriority = true;
    orderAmtCd.cellClass = ["blotter_amt_align" + " " + STRONG_CELL_CLASS];
    orderAmtCd.valueFormatter = this.formatAmts;
    orderAmtCd.columnFilterType = ColumnFilterType.INT;
    orderAmtCd.columnFilterData = null;
    orderAmtCd.valueGetter = function (params) {
      const risk: BmarkRiskData = params.data.risk;
      return risk.riskAmount;
    };
    orderAmtCd.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return this.orderAmountHelper.formatAmount(
        risk.fixedCcy,
        risk.riskAmount,
        risk.isSwap,
        risk.nearAmount,
        risk.farAmount,
      );
    };
    orderAmtCd.tooltipValueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk) {
        return this.orderAmountHelper.formatAmountTooltip(
          risk.riskAmount,
          risk.isSwap,
          risk.nearAmount,
          risk.farAmount,
          risk.fixedCcy,
        );
      }
      return "-";
    };

    if (!this.restoreFromConfig(orderAmtCd)) {
      orderAmtCd.position = this.position.next();
    }

    return orderAmtCd;
  }

  createNPVOrderAmtColumn(orderAmtCdCol?: BfxColDef): BfxColDef {
    let npvOrderAmtCd: BfxColDef = this.initColdDef();
    npvOrderAmtCd.headerName = "NPV Order Amt";
    npvOrderAmtCd.field = "npvOrderQty";
    npvOrderAmtCd.headerTooltip = "NPV Order Amount (Dealt)";
    npvOrderAmtCd.indexType = IndexType.NaturalString;
    npvOrderAmtCd.minWidth = 90;
    npvOrderAmtCd.width = this.eightWs;
    npvOrderAmtCd.isPriority = true;
    npvOrderAmtCd.cellClass = ["blotter_amt_align" + " " + STRONG_CELL_CLASS];
    npvOrderAmtCd.valueFormatter = this.formatAmts;
    npvOrderAmtCd.columnFilterType = ColumnFilterType.INT;
    npvOrderAmtCd.columnFilterData = null;
    npvOrderAmtCd.valueGetter = function (params) {
      const risk: BmarkRiskData = params.data.risk;
      return risk.npvRiskAmountSigned;
    };
    npvOrderAmtCd;
    npvOrderAmtCd.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;

      if (!risk.useNPV) {
        return "-";
      }

      return this.orderAmountHelper.formatAmount(
        risk.fixedCcy,
        risk.npvRiskAmountSigned,
        false,
        risk.nearNPVAmount,
        risk.farNPVAmount,
        false,
        true,
      );
    };

    npvOrderAmtCd.tooltipValueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk) {
        if (!risk.useNPV) {
          return "-";
        }

        return this.orderAmountHelper.formatAmountTooltip(
          risk.npvRiskAmountSigned,
          false,
          risk.nearNPVAmount,
          risk.farNPVAmount,
          risk.fixedCcy,
          true,
        );
      }
      return "-";
    };

    if (!this.restoreFromConfig(npvOrderAmtCd)) {
      if (orderAmtCdCol != null && orderAmtCdCol.position != null && orderAmtCdCol.position > 0) {
        npvOrderAmtCd.position = orderAmtCdCol.position + 1;
      } else {
        npvOrderAmtCd.position = this.position.next();
      }
    }

    return npvOrderAmtCd;
  }

  createIntentColumn(): BfxColDef {
    let intentCol: BfxColDef = this.initColdDef();

    intentCol.headerName = "Dir";
    intentCol.field = COLUMN_DEALT_DIR_DATA;
    intentCol.indexType = IndexType.String;
    intentCol.headerTooltip = "Direction (Dealt)";
    intentCol.minWidth = this.twoWs;
    intentCol.width = this.fourWs;
    intentCol.cellRenderer = "intentRenderer";
    intentCol.cellClass = "blotter_center_align_col blotter_priority_col intent_col";
    intentCol.isPriority = true;
    intentCol.columnFilterType = ColumnFilterType.NONE;
    intentCol.columnFilterData = CubiqTS.OrderIntent;
    intentCol.valueGetter = function (params) {
      let order: BmarkOrderData = params.data;
      const risk = order.risk;
      if (risk) {
        return ProductIntentRenderer.render(
          risk.clientIntent,
          risk.fixedCcy,
          order.product,
          order.requestedOrderType,
          risk.allocations,
          risk,
        );
      }
      return "";
    };
    if (!this.restoreFromConfig(intentCol)) {
      intentCol.position = this.position.next();
    }

    return intentCol;
  }

  createProductColumn(): BfxColDef {
    let prodCol: BfxColDef = this.initColdDef();
    prodCol.headerName = "Product";
    prodCol.field = COLUMN_ORDER_PRODUCT_PRODUCT;
    prodCol.alias = [COLUMN_ORDER_PRODUCT_PRODUCT, COLUMN_INTER_ORDER_PRODUCT];
    prodCol.indexType = IndexType.String;
    prodCol.cellClass = ["blotter_left_align_col"];
    prodCol.minWidth = this.oneWs;
    prodCol.width = this.fourWs;
    prodCol.columnFilterType = ColumnFilterType.ENUM;
    prodCol.columnFilterData = CubiqTS.Product;
    prodCol.valueGetter = function (params) {
      return ProductRenderer.render(
        params.data.product,
        params.data.requestTrackingType,
        params.data.orderType,
      );
    };
    prodCol.cellRenderer = "productRenderer";
    if (!this.restoreFromConfig(prodCol)) {
      prodCol.hide = true;
      prodCol.position = this.position.next();
    }
    prodCol.tooltipValueGetter = (params) => this.getProductTooltip(params);

    return prodCol;
  }

  private getProductTooltip(params: ITooltipParams) {
    const order: BmarkOrderData = params.data;
    if (order?.multiAllocation) {
      return order.createProductTooltip(this.orderAmountHelper, this.myValueDateHelper);
    } else {
      return params.value;
    }
  }

  protected processQuickFilterColumns(tableDefinition: TableDefinition): void {
    tableDefinition.columns.forEach((column: BfxColDef) => {
      if (column.getQuickFilterText == null) {
        column.getQuickFilterText = this.quickFilterText.bind(this);
      }
    });
  }

  public quickFilterText(params: GetQuickFilterTextParams): string {
    let value: string = null;

    if (
      params.column.isVisible() &&
      params.value != null &&
      typeof params.value !== "string" &&
      typeof params.colDef.valueFormatter === "function"
    ) {
      if (params.value instanceof CubiqTS.BigNumber) {
        value = params.value.toString();
      } else {
        value = params.colDef.valueFormatter(params as ValueFormatterParams);
      }
    } else if (params.column.isVisible() && params.value != null) {
      value = params.value + "";
    }

    return value;
  }

  public getBlotterType() {
    return BlotterType.ORDER;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }

  /**
   * Helper functions
   */

  private mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != null) {
        super.determineDefaultConfig(savedSettings);
      }
    } else {
      logger.out.warn(
        "BmarkClientOrdersTableDefinitionFactory --> mapUserSettings no profile value " +
          profileValue,
      );
    }
  }

  /*     public getBlotterVersion(): string {
        return super.getVersionIdentifier();
    } */

  protected getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}bmarkClientOrders`;
  }

  protected getFixingTimeValue(
    order: any,
    format: string,
    convertToUTC: boolean,
    fullDateTimeUTC: boolean,
    timeZone: boolean = true,
  ): string {
    let fixingDate: CubiqTS.Moment = order[FIXING_DATE_COLUMN_PROPERTY];
    let reqFixingTimeProperty = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
    );
    let reqFixingDateProperty = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
    );
    let reqTimeZoneCodeProperty: any = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
    );
    let reqFormattedDateProperty: string = CubiqTS.moment(reqFixingDateProperty).format(format);
    let reqFixingDateFull: string = FixingTimeAdapter.transformDateStringToMomentFormatString(
      reqFixingDateProperty,
      reqFixingTimeProperty,
    );

    if (convertToUTC) {
      if (fullDateTimeUTC) {
        return (
          CubiqTS.moment.utc(fixingDate).format(format) +
          " " +
          TIME_ZONE_UTC +
          " " +
          reqFormattedDateProperty
        );
      }
      return CubiqTS.moment.utc(fixingDate).format(format);
    }

    if (reqFixingDateFull) {
      return (
        CubiqTS.moment.utc(reqFixingDateFull).format(format) +
        (timeZone ? " " + reqTimeZoneCodeProperty : "")
      );
    }

    return EMPTY_TEXT_VALUE;
  }

  public formatRate(params: ValueFormatterParams) {
    if (params.value == null) {
      return EMPTY_AMT_RATE_VALUE;
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let order: BmarkOrderData = params.data;
    return this.formatRiskRate(order.risk, value);
  }

  private formatRiskRate(risk: BmarkRiskData, value: CubiqTS.BigNumber): string {
    let minPrecision: number = risk ? risk.getMinPrecision(this._ccyPairLookupService) : -1;
    return this.formatNumber(value, minPrecision);
  }

  private formatSwapPoints(
    risk: BmarkRiskData,
    nearPts: CubiqTS.BigNumber,
    farPts: CubiqTS.BigNumber,
  ): string {
    return this.formatSwapValues(risk, nearPts, farPts);
  }

  private formatSwapAllIn(
    risk: BmarkRiskData,
    nearAllIn: CubiqTS.BigNumber,
    farAllIn: CubiqTS.BigNumber,
  ): string {
    return this.formatSwapValues(risk, nearAllIn, farAllIn);
  }

  private formatSwapValues(
    risk: BmarkRiskData,
    nearValue: CubiqTS.BigNumber,
    farValue: CubiqTS.BigNumber,
  ): string {
    if (nearValue && farValue && risk) {
      let minPrecision: number = risk.getMinPrecision(this._ccyPairLookupService);
      const nearStr = this.formatNumber(nearValue, minPrecision);
      const farStr = this.formatNumber(farValue, minPrecision);
      return `${nearStr} / ${farStr}`;
    }
    return EMPTY_TEXT_VALUE;
  }

  private formatPoints(risk: BmarkRiskData, points: CubiqTS.BigNumber): string {
    if (points && risk) {
      let minPrecision: number = risk.getMinPrecision(this._ccyPairLookupService);
      return this.formatNumber(points, minPrecision);
    }
    return EMPTY_TEXT_VALUE;
  }

  private formatNumber(numValue: CubiqTS.BigNumber, minPrecision: number): string {
    let value: string = EMPTY_TEXT_VALUE;

    if (numValue) {
      /// Special constant in Bmark Risk to indicate number represents multiple values
      if (numValue === MULTIPLE_FWDS) {
        return STR_MULTIPLE;
      }
      if (minPrecision == -1) {
        return numValue?.toString() ?? "";
      }

      let currentDecs: number = numValue.decimalPlaces();
      if (currentDecs < minPrecision) {
        value = numValue.toFixed(minPrecision);
      } else {
        value = numValue.toFixed(currentDecs);
      }
    }

    return value;
  }

  protected formatDate(format: string, params: ValueFormatterParams): string {
    if (params.value && params.value.local) return params.value.local().format(format);

    return EMPTY_TEXT_VALUE;
  }

  async getBmarkOrderDisplayName() {
    this.bmarkShortNm = await this.getBMShortNameFromSysConfig();
  }

  private getBMShortNameFromSysConfig() {
    if (this._sysConfigService) {
      return new Promise<string>((res, rej) => {
        this._sysConfigService
          .getSystemConfig(SysFeatures.fixingOrderShortName)
          .then((value) => {
            let returnValue = value != null && value.length > 0 ? value : BMARK_DEFAULT_NAME;
            res(returnValue);
          })
          .catch((error) => {
            logger.out.error(`OrderTypeAdapter : getSystemConfig :
                        failed to get system config for: ${SysFeatures.fixingOrderShortName}`);
            rej(error.errorMessages);
          });
      });
    }
  }

  /**
   * Market Pts column
   */
  private createMarketPtsColumn(): BfxColDef {
    let col: BfxColDef = this.initColdDef();

    col.headerName = "Market Pts";
    col.field = "marketPts";

    col.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return risk.getMarketPts();
    };

    col.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        const near = risk.getNearMarketPts();
        const far = risk.getFarMarketPts();
        return this.formatSwapPoints(risk, near, far);
      } else if (params.value) {
        const pts = risk.getMarketPts();
        return this.formatPoints(risk, pts);
      }
      return EMPTY_TEXT_VALUE;
    };

    this.addFWdPriceCol(col);
    return col;
  }

  /**
   * Market All-in column
   */
  private createMarketAllInColumn(): BfxColDef {
    let col: BfxColDef = this.initColdDef();

    col.headerName = "Market All-In";
    col.field = "marketAllIn";

    col.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return risk.getMarketAllIn();
    };

    col.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        const near = risk.getNearMarketAllIn();
        const far = risk.getFarMarketAllIn();
        return this.formatSwapAllIn(risk, near, far);
      } else if (params.value) {
        const pts = risk.getMarketAllIn();
        return this.formatRiskRate(risk, pts);
      }
      return EMPTY_TEXT_VALUE;
    };

    this.addFWdPriceCol(col);
    return col;
  }

  /**
   * Client Pts column
   */
  private createClientPtsColumn(): BfxColDef {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Client Pts";
    col.field = "clientPts";

    col.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return risk.getClientPts();
    };

    col.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        const near = risk.getNearClientPts();
        const far = risk.getFarClientPts();
        return this.formatSwapPoints(risk, near, far);
      } else if (params.value) {
        const pts = risk.getClientPts();
        return this.formatPoints(risk, pts);
      }
      return EMPTY_TEXT_VALUE;
    };

    this.addFWdPriceCol(col);
    return col;
  }

  /**
   * Client All-in column
   */
  private createClientAllInColumn(): BfxColDef {
    let col: BfxColDef = this.initColdDef();

    col.headerName = "Client All-In";
    col.field = "clientAllIn";

    col.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return risk.getClientAllIn();
    };

    col.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        const near = risk.getNearClientAllIn();
        const far = risk.getFarClientAllIn();
        return this.formatSwapAllIn(risk, near, far);
      } else if (params.value) {
        const pts = risk.getClientAllIn();
        return this.formatRiskRate(risk, pts);
      }
      return EMPTY_TEXT_VALUE;
    };

    this.addFWdPriceCol(col);
    return col;
  }

  /**
   * Market Swap Points column
   */
  private createMarketSwapPtsColumn(): BfxColDef {
    let col: BfxColDef = this.initColdDef();

    col.headerName = "Market Swap Pts";
    col.field = "marketSwapPts";

    col.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        return risk.getMarketSwapPts();
      }
      return null;
    };

    col.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        const value = risk.getMarketSwapPts();
        return this.formatRiskRate(risk, value);
      }
      return EMPTY_TEXT_VALUE;
    };

    this.addFWdPriceCol(col);
    return col;
  }

  /**
   * Client Swap Points column
   */
  private createClientSwapPtsColumn(): BfxColDef {
    let col: BfxColDef = this.initColdDef();

    col.headerName = "Client Swap Pts";
    col.field = "clientSwapPts";

    col.valueGetter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        return risk.getClientSwapPts();
      }
      return null;
    };

    col.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      if (risk.isSwap) {
        const value = risk.getClientSwapPts();
        return this.formatRiskRate(risk, value);
      }
      return EMPTY_TEXT_VALUE;
    };

    this.addFWdPriceCol(col);
    return col;
  }

  /**
   * Shared code for formatting and adding a non-visible price column
   * @param col
   */
  private addFWdPriceCol(col: BfxColDef) {
    col.minWidth = this.oneWs;
    col.width = this.sevenWs;
    col.cellClass = ["blotter_amt_align"];
    col.indexType = IndexType.Double;
    col.columnFilterType = ColumnFilterType.NONE;
    this.position.next();
    if (!this.restoreFromConfig(col)) {
      col.hide = true;
      col.position = this.position.current();
    }
  }
}
